import React from 'react';

import type { FmMapFilterValues, FmMapWorkspaceDescriptor } from './FmMapWorkspaceRoot.types';
import { getMapSlot } from '@/shared/config/mapSlotRegistry';
import { FmMapCanvas, FmMapStage } from '@/shared/ui/FmMapStage';

function selectedStandId(selection: FmMapWorkspaceDescriptor['selection']): string | undefined {
  if (selection.kind === 'stand') return selection.standId;
  return selection.kind === 'plot' ? (selection.standId ?? undefined) : undefined;
}

function collectionPlotIds(descriptor: FmMapWorkspaceDescriptor): string[] {
  const selectedFeatureIds = new Set(
    descriptor.panels
      .filter((panel) => panel.kind === 'collection')
      .flatMap((panel) =>
        panel.records
          .filter((record) => panel.selectedIds.includes(record.id))
          .flatMap((record) => (record.featureId ? [record.featureId] : [])),
      ),
  );
  return descriptor.features
    .filter((feature) => feature.kind === 'plot' && selectedFeatureIds.has(feature.id))
    .map((feature) => feature.plotId ?? feature.id);
}

function highlightedPlotIds(
  descriptor: FmMapWorkspaceDescriptor,
  selectedStand: string | undefined,
): readonly string[] | undefined {
  const selected = collectionPlotIds(descriptor);
  if (selected.length > 0) return selected;
  if (descriptor.selection.kind === 'campaign') {
    return descriptor.selection.plotIds;
  }
  if (descriptor.selection.kind !== 'stand') return undefined;
  return descriptor.features
    .filter((feature) => feature.kind === 'plot' && feature.standId === selectedStand)
    .map((feature) => feature.plotId ?? feature.id);
}

interface WorkspaceMapProps {
  descriptor: FmMapWorkspaceDescriptor;
  filterValues: FmMapFilterValues;
  isPreview: boolean;
  propertyId: string;
  selectFeature: (
    featureId: string,
    expectedKind?: 'stand' | 'plot',
    featurePropertyId?: string,
  ) => void;
}

export const FmMapWorkspaceMap: React.FC<WorkspaceMapProps> = ({
  descriptor,
  filterValues,
  isPreview,
  propertyId,
  selectFeature,
}) => {
  const MapSlot = getMapSlot(descriptor.mapSlotKey);
  const standId = selectedStandId(descriptor.selection);
  const slotProps = {
    propertyId: propertyId || undefined,
    selectedFeatureId: descriptor.selection.kind === 'none' ? undefined : descriptor.selection.id,
    selectedFeatureKind:
      descriptor.selection.kind === 'none' || descriptor.selection.kind === 'campaign'
        ? undefined
        : descriptor.selection.kind,
    selectedPlotId: descriptor.selection.kind === 'plot' ? descriptor.selection.plotId : undefined,
    selectedStandId: standId,
    highlightedPlotIds: highlightedPlotIds(descriptor, standId),
    measurementPlotRefs: descriptor.features
      .filter((feature) => feature.kind === 'plot')
      .map((feature) => ({
        id: feature.plotId ?? feature.id,
        label: feature.label,
        mappingStatus: feature.mappingStatus ?? 'unmapped',
        standId: feature.standId ?? undefined,
      })),
    onFeatureSelect: selectFeature,
    onPlotSelect: (plotId: string) => selectFeature(plotId, 'plot'),
    onStandSelect: (id: string, standPropertyId?: string) =>
      selectFeature(id, 'stand', standPropertyId),
    layerIds: descriptor.layers.filter((layer) => layer.visible).map((layer) => layer.id),
    baseMapId: descriptor.activeBaseMapId,
    interactionMode: 'explore' as const,
    workspaceMode: descriptor.mode,
    viewportRef: descriptor.viewport,
    geometryRefs: descriptor.geometryRefs,
    sourceId: descriptor.source.id,
    workspaceFilters: filterValues,
  };
  if (MapSlot && !isPreview && !descriptor.mapData) {
    throw new Error('Production map workspace requires a descriptor-owned render snapshot.');
  }
  const map =
    MapSlot && isPreview ? (
      <MapSlot {...slotProps} mapData={null} preview />
    ) : MapSlot && descriptor.mapData ? (
      <MapSlot {...slotProps} mapData={descriptor.mapData} preview={false} />
    ) : null;
  const fallback = (
    <output className="fm-map-workspace__fallback">{descriptor.copy.rendererUnavailable}</output>
  );
  return (
    <FmMapStage fill className="fm-map-workspace__stage" data-testid="map-workspace-stage">
      <FmMapCanvas dark data-testid="map-workspace-canvas">
        <React.Suspense fallback={fallback}>{map ?? fallback}</React.Suspense>
      </FmMapCanvas>
    </FmMapStage>
  );
};
